/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/assistantPageContextLabel.ts
 * PURPOSE: Human-readable page label for the AI assistant (document title + nav fallback).
 * DEPENDENCIES: `config/nav` (main rail); `features/crm/constants/crmTabs` (CRM tab labels: sidebar links only `/crm`, so nested CRM paths need tab metadata).
 * PUBLIC_API: `getAssistantPageHeadline`, type `AssistantPageContextInput`
 * SIDE_EFFECTS: None (pure functions).
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 69/150
 * RECENT: 2026-05-07 [2.2.853] — `navFallbackLabel` uses `matchCrmTabForPathname` for `/crm` and `/crm/*` so assistant headline matches `CrmTabNav` after sidebar CRM children removal [2.2.778]. Root `CHANGELOG` [2.2.853].
 */

import { mainNavItems } from '../../../config/nav';
import { matchCrmTabForPathname } from '../../../features/crm/constants/crmTabs';
import { isLikelyAccountDetailId } from '../../../routes/builders';

function stripTitleBranding(title: string): string {
  return title
    .replace(/\s*[|\u2014\u2013–-]\s*Brazn\s*$/i, '')
    .replace(/\s*\|\s*Brazn\s*Hub\s*$/i, '')
    .trim();
}

function navFallbackLabel(pathname: string): string | null {
  const norm = (pathname.replace(/\/$/, '') || pathname).trim() || '/';
  if (norm === '/crm' || norm.startsWith('/crm/')) {
    return matchCrmTabForPathname(pathname).label;
  }

  const accountDetailSeg = norm.match(/^\/accounts\/([^/]+)$/)?.[1];
  if (accountDetailSeg && isLikelyAccountDetailId(accountDetailSeg)) {
    return matchCrmTabForPathname(pathname).label;
  }

  const flat: { path: string; label: string }[] = [];
  for (const item of mainNavItems) {
    flat.push({ path: item.path, label: item.label });
    for (const c of item.children ?? []) {
      flat.push({ path: c.path, label: c.label });
    }
  }
  let best: { path: string; label: string } | null = null;
  for (const item of flat) {
    if (item.path === '/') {
      if (pathname === '/' || pathname === '') {
        best = item;
      }
      continue;
    }
    if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
      if (!best || item.path.length > best.path.length) {
        best = item;
      }
    }
  }
  return best?.label ?? null;
}

export type AssistantPageContextInput = {
  path: string;
  title?: string;
};

/**
 * Prefer cleaned `document.title`; fall back to longest matching main-nav label; then raw path.
 */
export function getAssistantPageHeadline(ctx: AssistantPageContextInput | null | undefined): string {
  if (!ctx?.path?.trim()) return '';
  const pathname = (ctx.path.split('?')[0]?.split('#')[0] ?? ctx.path).trim() || '/';
  const rawTitle = ctx.title?.trim() ?? '';
  const cleaned = rawTitle ? stripTitleBranding(rawTitle) : '';
  if (cleaned.length > 0 && !/^brazn$/i.test(cleaned)) {
    return cleaned;
  }
  return navFallbackLabel(pathname) ?? pathname;
}
